import { ReactNode, useEffect, useMemo } from 'react';

import { GetChecksResponseDTO, GetUnitsResponseDTO, MODULE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { ColumnFiltersState, RowSelectionState } from '@tanstack/react-table';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import UndoIcon from '@/components/icons/undo';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { useToast } from '@/components/ui/toaster';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useWorkspaceNavigate } from '@/utils/routes';

import { createCheckTableColumns } from './table-meta';

export function ScheduleChecksTable({
  notFoundDisplay,
  data,
  loading,
  onRowSelectionChange,
  rowSelection,
  columnFilters,
  onColumnFiltersChange,
  sorting: sortingProp,
  grouping,
  columnOrder,
  columnVisibility,
  onSortingChange,
  onRowClick,
  units,
}: {
  notFoundDisplay: ReactNode;
  data: GetChecksResponseDTO['data'];
  loading: boolean;
  onRowSelectionChange: (selectedRows: RowSelectionState) => void;
  rowSelection: RowSelectionState;
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (columnFilters: ColumnFiltersState) => void;
  sorting: { key?: string; direction?: 'asc' | 'desc' };
  grouping: string[];
  columnOrder: Array<{ id: string; active: boolean }>;
  columnVisibility: Record<string, boolean>;
  onSortingChange: (sorting: { key?: string; direction?: 'asc' | 'desc' }) => void;
  onRowClick?: (row: GetChecksResponseDTO['data'][number]) => void;
  units: GetUnitsResponseDTO['data'];
}) {
  const { syncModuleColumns } = useModulesStore();
  const navigate = useWorkspaceNavigate();
  const toast = useToast();

  const sorting = useMemo(
    () => (sortingProp.key ? [{ id: sortingProp.key, desc: sortingProp.direction === 'desc' }] : []),
    [sortingProp.key, sortingProp.direction],
  );

  async function handleWorkOrderClick(workOrderId: string) {
    try {
      const workOrderData = (await (await ApiService.GetWorkOrderById({ workOrderId })).json()).workOrder;
      navigate('/work-orders', { state: { workOrder: workOrderData } });
    } catch (error) {
      console.error('Error fetching work order:', error);
      toast.error('Failed to load work order');
    }
  }

  useEffect(() => {
    syncModuleColumns(MODULE.ScheduleChecks, createCheckTableColumns());
  }, [syncModuleColumns]);

  return (
    <div className="flex-1 bg-white">
      <TablePageTable<GetChecksResponseDTO['data'][number]>
        data={data}
        columns={createCheckTableColumns({ units, onWorkOrderClick: handleWorkOrderClick })}
        notFoundDisplay={notFoundDisplay}
        loading={loading}
        onSortingChange={onSortingChange}
        sorting={sorting}
        grouping={grouping}
        columnOrder={(columnOrder || []).map((column) => column.id)}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        columnVisibility={columnVisibility}
        columnFilters={columnFilters}
        onColumnFiltersChange={onColumnFiltersChange}
        onRowClick={onRowClick}
      />
    </div>
  );
}

export function ChecksNoResultsDisplay() {
  return (
    <div className="absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-sm">No checks match your search</span>
    </div>
  );
}

export function ChecksNoFiltersMatchDisplay({ removeColumnFilters }: { removeColumnFilters: () => void }) {
  return (
    <div className="bg-grey-1 absolute inset-0 flex h-full w-full flex-col items-center justify-center gap-4 p-4">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl font-medium">No checks match your filters</span>
      <span className="text-secondary-text text-sm">Try adjusting your column filters</span>
      <div className="pointer-events-auto flex gap-2">
        <Button variant="grey" onClick={removeColumnFilters} className="gap-1 font-normal">
          <UndoIcon />
          Remove all filters
        </Button>
      </div>
    </div>
  );
}

export function ChecksEmptyDisplay() {
  return (
    <div className="absolute inset-0 m-auto flex w-[383px] flex-col items-center justify-center gap-[20px]">
      <img src={manholeShrugVector} alt="Manhole shrug" className="h-28" />
      <span className="text-xl leading-[30px] font-medium">No checks available</span>
      <div className="flex flex-col gap-0 text-center">
        <span className="text-secondary-text text-sm">Checks will appear here when schedules are deployed to assets. Deploy a schedule to get started.</span>
      </div>
    </div>
  );
}
